import React, { useRef, useState } from "react";
import {
  animate,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useTransform,
} from "motion/react";
import styled, {
  css,
  DefaultTheme,
  keyframes,
  useTheme,
} from "styled-components";
import {
  NEW_LOOK_REFRESH_SPINNER_OFFSET,
  NEW_LOOK_REFRESH_SPINNER_SIZE,
} from "common/constants";
import { getNewLookTokens } from "common/NewLook/tokens";
import {
  getPullProgress,
  RefreshSpinnerProps,
} from "common/Scroll/pullToRefresh";
import {
  getArcDash,
  getArrowPath,
  getDiscOpacity,
  getDiscScale,
  getPullTurn,
  MAX_ARC,
  RING_CENTER,
  RING_CIRCUMFERENCE,
  RING_RADIUS,
  RING_STROKE,
} from "common/Lists/refreshSpinnerGeometry";
import { hexToRGB } from "@shared/utils/misc";
import { PULL_TO_REFRESH_ID } from "testIds";

const SPINNER_SIZE = 20;
const DIM_OPACITY = 0.5;
const ARM_POP = 1.08;
const ARM_POP_TRANSITION = { duration: 0.28, ease: "easeOut" } as const;
const SHORT_ARC = 0.08;
const LIGHT_DISC_SHADOW =
  "0 0 0 0.5px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.12), 0 4px 14px rgba(16, 24, 40, 0.14)";
const DARK_DISC_SHADOW =
  "0 0 0 1px rgba(255, 255, 255, 0.09), 0 2px 4px rgba(0, 0, 0, 0.32), 0 6px 16px rgba(0, 0, 0, 0.36)";

const getPullState = (isRefreshing: boolean, armed: boolean) => {
  if (isRefreshing) return "refreshing";
  return armed ? "armed" : "idle";
};

const NewLookRefreshSpinner = ({
  distance,
  finish,
  isRefreshing,
  model,
}: RefreshSpinnerProps) => {
  const tokens = getNewLookTokens(useTheme());
  const reducedMotion = useReducedMotion();
  const [armed, setArmed] = useState(false);
  const armedRef = useRef(false);
  const pop = useMotionValue(1);
  const progress = useTransform(distance, (value) =>
    getPullProgress(value, model)
  );
  useMotionValueEvent(progress, "change", (value) => {
    const isArmed = value >= 1;
    if (isArmed && !armedRef.current && !reducedMotion && !isRefreshing) {
      animate(pop, [1, ARM_POP, 1], ARM_POP_TRANSITION);
    }
    armedRef.current = isArmed;
    setArmed(isArmed);
  });
  const y = useTransform(
    distance,
    (value) => value - NEW_LOOK_REFRESH_SPINNER_OFFSET
  );
  const scale = useTransform(
    [distance, finish, pop],
    ([distanceValue, finishValue, popValue]: number[]) =>
      getDiscScale(distanceValue, finishValue, popValue)
  );
  const opacity = useTransform(
    [distance, finish],
    ([distanceValue, finishValue]: number[]) =>
      getDiscOpacity(distanceValue, finishValue)
  );
  const turn = useTransform(distance, (value) =>
    getPullTurn(value, model.threshold)
  );
  const dash = useTransform(progress, getArcDash);
  const arrow = useTransform(progress, getArrowPath);

  return (
    <Disc
      style={{ y, scale, opacity }}
      data-testid={PULL_TO_REFRESH_ID}
      data-state={getPullState(isRefreshing, armed)}
      aria-hidden={true}
    >
      <Turn style={{ rotate: turn }}>
        <Ring
          viewBox="0 0 24 24"
          $spinning={isRefreshing}
          $lit={armed || isRefreshing}
        >
          <Arc
            cx={RING_CENTER}
            cy={RING_CENTER}
            r={RING_RADIUS}
            fill="none"
            stroke={tokens.accent}
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            transform={`rotate(-90 ${RING_CENTER} ${RING_CENTER})`}
            style={{ strokeDasharray: dash }}
            $spinning={isRefreshing}
          />
          <Arrow d={arrow} fill={tokens.accent} $hidden={isRefreshing} />
        </Ring>
      </Turn>
    </Disc>
  );
};

export default NewLookRefreshSpinner;

const getDashKeyframe = (arc: number, offset: number) => `
  stroke-dasharray: ${arc * RING_CIRCUMFERENCE}
    ${(1 - arc) * RING_CIRCUMFERENCE};
  stroke-dashoffset: ${offset * RING_CIRCUMFERENCE};
`;
const getDiscBackground = (theme: DefaultTheme) => {
  const { panel } = getNewLookTokens(theme);
  if (!theme.isDarkMode) return panel;
  const lift = hexToRGB("#FFFFFF", 0.07);
  return `linear-gradient(${lift}, ${lift}), ${panel}`;
};

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;
const chase = keyframes`
  0% {
    ${getDashKeyframe(SHORT_ARC, 0.25)}
  }
  50% {
    ${getDashKeyframe(MAX_ARC, 0)}
  }
  100% {
    ${getDashKeyframe(SHORT_ARC, -0.75)}
  }
`;
const breathe = keyframes`
  50% {
    opacity: 0.45;
  }
`;

const Disc = styled(motion.div)`
  position: absolute;
  top: 0;
  left: calc(50% - ${NEW_LOOK_REFRESH_SPINNER_SIZE / 2}px);
  width: ${NEW_LOOK_REFRESH_SPINNER_SIZE}px;
  height: ${NEW_LOOK_REFRESH_SPINNER_SIZE}px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  pointer-events: none;
  background: ${(p) => getDiscBackground(p.theme)};
  box-shadow: ${(p) =>
    p.theme.isDarkMode ? DARK_DISC_SHADOW : LIGHT_DISC_SHADOW};
`;
const Turn = styled(motion.div)`
  width: ${SPINNER_SIZE}px;
  height: ${SPINNER_SIZE}px;
`;
const Ring = styled.svg<{ $spinning: boolean; $lit: boolean }>`
  width: ${SPINNER_SIZE}px;
  height: ${SPINNER_SIZE}px;
  display: block;
  overflow: visible;
  opacity: ${(p) => (p.$lit ? 1 : DIM_OPACITY)};
  transition: opacity 0.2s ease;
  ${(p) =>
    p.$spinning &&
    css`
      animation: ${spin} 2s linear infinite;
      @media (prefers-reduced-motion: reduce) {
        animation: ${breathe} 1.4s ease-in-out infinite;
      }
    `};
`;
const Arc = styled(motion.circle)<{ $spinning: boolean }>`
  ${(p) =>
    p.$spinning &&
    css`
      animation: ${chase} 1.5s ease-in-out -0.75s infinite;
      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `};
`;
const Arrow = styled(motion.path)<{ $hidden: boolean }>`
  opacity: ${(p) => (p.$hidden ? 0 : 1)};
  transition: opacity 0.12s ease-out;
`;
